import CloseIcon from '@mui/icons-material/Close'
import Drawer, { drawerClasses } from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import React, { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { LAYOUT_ELEMENT_ID } from '../constants/layout'
import useDimensions from '../hooks/useDimensions'
import useLockedBody from '../hooks/useLockedBody'
import { HeaderContainer } from './Header'

const StyledDrawer = styled(Drawer)`
  .${drawerClasses.paper} {
    width: 400px;

    ${props => props.theme.breakpoints.down('sm')} {
      width: 100%;
    }
  }
`

const Header = styled(HeaderContainer)`
  position: fixed;
  width: inherit;

  /* Position header above sidebar content */
  z-index: 1;
`

type SidebarProps = {
  children: ReactNode
  open: boolean
  setOpen: (show: boolean) => void
  footer?: ReactNode
  openButton: ReactElement
  className?: string
}

const Sidebar = ({ children, open, setOpen, footer, openButton, className }: SidebarProps): ReactElement | null => {
  const { headerHeight } = useDimensions()
  const { t } = useTranslation()
  useLockedBody(open)

  // This is necessary to ensure the theme is correctly applied to the drawer content
  const drawerContainer = document.getElementById(LAYOUT_ELEMENT_ID)

  return (
    <>
      {openButton}
      <StyledDrawer
        open={open}
        onClose={() => setOpen(false)}
        container={drawerContainer}
        className={className}
        anchor='right'
        // Locking scroll causes the headroom to disappear when opening the drawer if scrolled down
        disableScrollLock
        // Restoring focus when closing the drawer to a sticky element (headroom) scrolls the content to the top
        disableRestoreFocus>
        <Header>
          <Stack sx={{ minHeight: headerHeight, justifyContent: 'center', alignItems: 'flex-end', paddingInline: 1 }}>
            <IconButton onClick={() => setOpen(false)} aria-label={t($ => $.common.actions.close)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Header>
        <Stack sx={{ marginTop: `${headerHeight}px`, padding: 2, height: '100%' }}>{children}</Stack>
        {footer}
      </StyledDrawer>
    </>
  )
}

export default Sidebar
